import { useEffect, useId, useRef } from "react";

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileWidgetOptions = {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type TurnstileAPI = {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement | string, options: TurnstileWidgetOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
    __gnpfTurnstilePromise?: Promise<void>;
  }
}

type Props = {
  siteKey: string;
  action?: string;
  onToken: (token: string) => void;
  onExpire: () => void;
  onError: () => void;
};

export function TurnstileBox({ siteKey, action = "customer_booking", onToken, onExpire, onError }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const id = useId().replaceAll(":", "");

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) return;
    loadTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        window.turnstile.ready(() => {
          if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current) return;
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            action,
            callback: onToken,
            "expired-callback": onExpire,
            "error-callback": onError,
          });
        });
      })
      .catch(onError);
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [action, onError, onExpire, onToken, siteKey]);

  if (!siteKey) {
    return (
      <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning-text">
        Thiếu cấu hình Turnstile site key cho trang booking.
      </div>
    );
  }

  return <div id={`turnstile-${id}`} ref={containerRef} className="min-h-[70px]" />;
}

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (window.__gnpfTurnstilePromise) return window.__gnpfTurnstilePromise;
  window.__gnpfTurnstilePromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  });
  return window.__gnpfTurnstilePromise;
}
